import { createSlice } from '@reduxjs/toolkit';
import { createSliceHook, makeActionCreator } from '../helpers';
import {
  QueriesFetchAction,
  QueriesFetchSuccessAction,
  QueriesState,
  QueryFetchAction,
  QueryFetchSuccessAction,
  SetQueriesFilterAction,
} from './queries.types';

const initialState: QueriesState = {
  loading: false,
  error: undefined,
  clinicalTrialId: null,
  data: null,
  filters: {},
  currentQuery: null,
  currentQueryLoading: false,
  currentQueryError: undefined,
};

const { actReq, actReject, actResolve } = makeActionCreator<QueriesState>();

/**
 * Re-scope the list state to another clinical trial
 *
 * Plain terms
 * -----------
 * Queries belong to one study. When the user moves to another study, the list
 * and the filters of the previous study are thrown away so nothing of it is
 * shown — or sent to the server — under the new study's name.
 *
 * Technical
 * ---------
 * `currentQuery` is deliberately untouched: the detail page
 * (`ReadOnlyQueryPage`) owns that lifecycle and clears it on unmount via
 * `clearCurrentQuery`.
 */
const scopeToClinicalTrial = (state: QueriesState, clinicalTrialId: string) => {
  state.clinicalTrialId = clinicalTrialId;
  state.data = null;
  state.error = undefined;
  state.filters = {};
};

export const queriesSlice = createSlice({
  name: 'queries',
  initialState,
  reducers: {
    /**
     * Start loading the list of the clinical trial addressed by the route
     *
     * Plain terms
     * -----------
     * Every list request records which study it was made for, so the app can
     * always tell whether the queries it holds belong to the study the user is
     * looking at. Asking for another study's list drops the current one right
     * away, instead of leaving it on screen until the answer arrives.
     *
     * Technical
     * ---------
     * Replaces the generated `actReq` to stamp `clinicalTrialId` and to act as
     * the last line of defence for any caller that fetches without calling
     * `resetQueries` first. The `loading` / `error` transition is identical to
     * `actReq`, and a fetch for the trial the data already belongs to leaves
     * `data` and `filters` untouched.
     */
    fetchQueries: (state, action: QueriesFetchAction) => {
      if (state.clinicalTrialId !== action.payload) {
        scopeToClinicalTrial(state, action.payload);
      }

      state.loading = true;
      state.error = undefined;
    },
    /**
     * Drop everything that belongs to the previously viewed clinical trial
     *
     * Plain terms
     * -----------
     * When the user opens the Queries tab of another study, the list, the
     * status filter and the Subject ID search start from scratch — nothing of
     * the previous study is shown or sent to the server.
     *
     * Technical
     * ---------
     * Dispatched by `ReadOnlyQueriesList` before the first request of a new
     * trial, so that request is issued with empty filters and page 1.
     */
    resetQueries: (state, action: QueriesFetchAction) => {
      scopeToClinicalTrial(state, action.payload);

      state.loading = false;
    },
    fetchQueriesSuccess: actResolve<QueriesFetchSuccessAction['payload']>(
      'data'
    ),
    fetchQueriesFailure: actReject<Error>(),
    fetchCurrentQuery: actReq<QueryFetchAction['payload']>(
      'currentQueryLoading'
    ),
    fetchCurrentQuerySuccess: actResolve<QueryFetchSuccessAction['payload']>(
      'currentQuery',
      'currentQueryLoading',
      'currentQueryError'
    ),
    fetchCurrentQueryFailure: actReject(
      'currentQueryError',
      'currentQueryLoading'
    ),
    clearCurrentQuery: state => {
      state.currentQuery = null;
      state.currentQueryLoading = false;
      state.currentQueryError = undefined;
    },
    setFilters: (state, action: SetQueriesFilterAction) => {
      const { filterQuery, filterValue } = action.payload;

      state.filters = {
        ...state.filters,
        [filterQuery]: filterValue,
      };
    },
    clearFilters: state => {
      state.filters = {};
    },
  },
});

export const actions = {
  ...queriesSlice.actions,
};

// Fall back to initialState so a role without this slice registered renders an
// empty table instead of crashing the app on `state.loading`.
export const useQueriesSlice = createSliceHook(
  queriesSlice.name,
  actions,
  initialState
);
